import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { environment } from '@lmc-env/environment';
import { Observable } from 'rxjs';

/**
 * Issue codes raised by the reconciliation audit. Mirrors ISSUE in
 * services/kapcharge-reconciliation.service.js on the backend, which owns the values.
 */
export type PaymentErrorIssue =
  | 'WRONG_TRANSACTION_ID'
  | 'MISSING_TRANSACTION_ID'
  | 'DUPLICATE_ID'
  | 'ORPHAN_REMOTE'
  | 'STATUS_MISMATCH'
  | 'NOT_FOUND_AT_KAPCHARGE'
  | 'AMBIGUOUS_MATCH'
  | 'WRONG_MERCHANT';

export type PaymentErrorSeverity = 'Critical' | 'Warning' | 'Info';

export interface PaymentErrorFinding {
  id: number;
  issue: PaymentErrorIssue;
  severity: PaymentErrorSeverity;

  /** Our KapchargeTransaction.id. Null for ORPHAN_REMOTE — there is no local row. */
  transactionId: number | null;
  batchId: string | null;
  loanNumber: string | null;
  /**
   * The borrower behind the loan number above, resolved on read from NLS (loanacct + cif)
   * rather than stored on the finding. Null when the loan is not in loanacct, or when the
   * finding names no loan at all.
   */
  customerName: string | null;
  localStatus: string | null;
  /**
   * KapchargeTransaction.error — why the submit failed. Populated only when localStatus is
   * 'Error'; the backend withholds it otherwise, since the column is not always cleared when
   * a row moves on and stale text would describe a failure that no longer applies.
   */
  localError: string | null;
  amountInCents: number | null;
  dueDate: string | null;
  transactionFlow: string | null;
  /** Our ID for the NLS payment (loanacct_payment_history.userdef01). */
  nlsTransactionId: string | null;
  /** The id our row holds. Null when it never recorded one. */
  kapchargeTransactionId: string | null;
  /** The id it should hold — the transaction KapCharge has for this loan, amount and due date. */
  expectedKapchargeTransactionId: string | null;
  expectedClientReference: string | null;
  /** The wallet the row's originator maps to, so it can be paired with remoteMerchantId. */
  expectedMerchantId: string | null;

  remoteClientReference: string | null;
  /** The loan KapCharge says the matched transaction belongs to. */
  remoteLoanNumber: string | null;
  remoteAmountInCents: number | null;
  remoteMerchantId: string | null;
  remoteStatus: string | null;
  remoteResponseCode: string | null;
  remoteCreatedDateTime: string | null;
  /**
   * The decline/return timestamp, which `returnedAt` is written from when the repair applies
   * this status. Carried on the finding so the repair needs no KapCharge call of its own.
   */
  remoteDeclinedDate: string | null;
  remoteCustomerName: string | null;

  detail: string | null;

}

/** What one scan says about itself — shown alongside the findings, not stored anywhere. */
export interface PaymentErrorScanSummary {
  rowsChecked: number;
  remoteSeen: number;
  findingCount: number;
  apiCalls: number;
  /** Wall-clock time the scan took on the server, excluding the caller's own network. */
  durationMs: number;
  /** What KapCharge actually put in transactionStatus — see the backend service's note. */
  statusFormat: string | null;
  /** Wallet/day windows where paging was cut short; findings for those days are unreliable. */
  truncatedDetail: string | null;
}

export interface PaymentErrorScanResponse {
  scannedDate: string;
  /**
   * Names this scan for the repair, which works off the copy the server kept rather than
   * anything sent back from here. Expires after 30 minutes, and a server restart clears it —
   * a repair against a scan that is gone answers 410 and has to be re-scanned.
   */
  scanId: string;
  /** Every finding for the day. The page filters and pages these in memory. */
  results: PaymentErrorFinding[];
  issueCounts: Partial<Record<PaymentErrorIssue, number>>;
  summary: PaymentErrorScanSummary;
}

/** What one run of the fix actually did. Every list may be empty. */
export interface FixReport {
  fixed: { transactionId: number; loanNumber: string | null; from: string | null; to: string }[];
  /**
   * Rows an id was taken back from, because ownership was proved to lie with another row.
   * Each now has no id and is repaired by the next run — a shifted submit response rotates
   * ids across rows, so this is the normal shape of the problem, not an edge case.
   */
  cleared: { transactionId: number; loanNumber: string | null; releasedId: string }[];
  /** Fixes deliberately not applied, each with the reason. */
  skipped: { transactionId: number; loanNumber: string | null; expected: string; reason: string }[];
}

/**
 * One payment moved off a returned status, for a person to check in NLS.
 *
 * The NSF is not reversed automatically. The payment is found in NLS by its userdef01
 * (`nlsTransactionId`), or by loan, due date and amount.
 */
export interface NsfReversal {
  transactionId: number;
  kapchargeTransactionId: string | null;
  loanNumber: string | null;
  acctrefno: number | null;
  /** The payment's userdef01 in NLS. */
  nlsTransactionId: string | null;
  amountInCents: number;
  dueDate: string | null;
  statusWas: string | null;
  returnCodeWas: string | null;
  statusNow: string | null;
}

/** One row of a status fix report. Mirrors describeResult in kapcharge-repair.service.js. */
export interface StatusFixRow {
  id: number;
  kapchargeTransactionId: string | null;
  loanNumber: string | null;
  amountInCents: number;
  /** Our status as it stood when the row was compared. */
  status: string | null;
  /** What the scan read off KapCharge. Equal to `status` on an `unchanged` row. */
  kapchargeStatus: string | null;
  returnCode: string | null;
  /**
   * unchanged      - we already agreed with KapCharge; nothing written
   * updated        - written
   * ignored        - dropped by the out-of-order guard: the row is already Returned,
   *                  TxnError or Cancelled, and those are final
   * failed         - the write or its NLS import threw
   * unknown-status - the scan read a status it could not compare, so none was applied
   */
  outcome: 'unchanged' | 'updated' | 'ignored' | 'failed' | 'unknown-status';
  /**
   * Came back Returned on a Collect-flow row, so an NSF applies in NLS — posted, or would
   * be. Every return counts, whatever the return code; a returned refund (Pay flow) does not.
   */
  nsf: boolean;
}

export interface StatusFixReport {
  date: string | null;
  elapsedSeconds: number;
  totals: { checked: number; recovered: number; nsfApplied: number; failed: number };
  results: StatusFixRow[];
  /**
   * Rows corrected off a return. Any NSF standing in NLS against them was NOT reversed — an
   * NSFPAYMENT import cannot be undone from here — so each is a payment for a person to
   * check and reverse if needed.
   */
  nsfReversals: NsfReversal[];
}

/** What one run of the repair did, both steps. */
export interface RepairResponse {
  idReport: FixReport;
  statusReport: StatusFixReport;
}

@Injectable({
  providedIn: 'root',
})
export class PaymentErrorsService {
  private readonly uri: string = `${environment.DO_URL}/v2/payment-errors`;

  constructor(private readonly http: HttpClient) {}

  /**
   * Runs the comparison for one due date and resolves with everything it found.
   *
   * The whole day comes back in one response; the page filters and pages it in memory. The
   * findings are also kept server-side for the repair — see `scanId`.
   *
   * @param day the due date to scan, YYYY-MM-DD
   * @param loanNumber restrict the scan to one loan
   */
  scan(day: string, loanNumber?: string): Observable<PaymentErrorScanResponse> {
    return this.http.post<PaymentErrorScanResponse>(`${this.uri}/scan`, {
      date: day,
      loanNumber: loanNumber?.trim() || null,
    });
  }

  /**
   * Runs the whole repair: transaction ids first, then the statuses that scan read for those
   * same rows, posting NSF into NLS for every row that came back Returned.
   *
   * The order matters and is enforced server-side. A row pointing at the wrong transaction
   * would otherwise have the wrong transaction's status mirrored onto it, which is how a
   * wrong NSF reaches a loan in the first place.
   *
   * **Only row ids go up.** The server still holds the scan and writes what *it* read, so the
   * repair makes no KapCharge call — which is what took it from minutes to seconds — and
   * nothing sent from here can decide what is written. Naming a row the scan did not propose a
   * repair for reaches nothing; it comes back in `idReport.skipped` saying so.
   *
   * A scan that has expired (30 minutes) or was lost to a restart answers **410**, and the
   * only way forward is to scan again.
   *
   * Rows already Returned are corrected too. The NSF in NLS is left standing — nothing can
   * reverse an NSFPAYMENT import — so `statusReport.nsfReversals` is the list a person must
   * work through by hand, and the caller has to put it in front of them.
   *
   * @param scanId the scan being repaired, from its response
   * @param transactionIds the rows to repair; omit to repair everything that scan proposes
   */
  repair(scanId: string, transactionIds?: number[]): Observable<RepairResponse> {
    return this.http.post<RepairResponse>(`${this.uri}/fix`, { scanId, transactionIds });
  }

}
